// ============================================================================
// LINKIFY — turning the URLs inside a block of PLAIN TEXT into things you can click.
//
// THE DEFECT THIS FIXES (owner screenshot, 2026-08-29): a GSI's announcement carried a Zoom link
// and a Google Forms URL, and both rendered as dead text. The owner underlined the Forms URL in
// red, which is the whole bug report — he could see the address and could not go to it.
//
// WHY IT IS DONE HERE AND NOT UPSTREAM. Canvas announcement bodies are HTML, and the Worker
// strips them to text (apps/worker/src/lib/html-text.ts) precisely so nothing in this app ever
// hands markup to `dangerouslySetInnerHTML`. Re-emitting `<a>` from the sanitizer would undo that
// in one line and put a professor's paste-from-Word back on the render path. So the sanitizer is
// untouched, and this file does the ONE thing the screen actually needs: it SPLITS TEXT INTO
// SPANS. Nothing here produces or parses HTML; the component renders React elements from the
// spans, and React escapes every character of the text as it always did.
//
// WHAT COUNTS AS A LINK: `http://` or `https://` and nothing else. `www.` is not matched, and
// neither is a bare domain — a sentence saying "see bcourses.berkeley.edu" is a sentence, and
// guessing a scheme for it is inventing a destination. `mailto:` is deliberately out too, for
// the smaller reason: nothing in the owner's real announcements uses one, and an untested branch
// is a branch that is wrong the first time it fires.
// ============================================================================

/** A run of ordinary prose. */
export interface TextSpan {
  kind: 'text'
  text: string
}

/** One URL, with the address to go to and the (possibly shortened) address to print. */
export interface LinkSpan {
  kind: 'link'
  /** The full URL, exactly as it appeared. What `href` and `title` both carry. */
  href: string
  /** What the reader sees. Equal to `href` unless it was too long to sit in a line of prose. */
  label: string
}

export type LinkifiedSpan = TextSpan | LinkSpan

/**
 * A URL run: the scheme, then everything up to whitespace or a character that cannot be inside
 * one on a page of prose.
 *
 * `<`, `>`, `"` and `'` end it because a body that came through the sanitizer can still contain
 * them as literal text (`html-text.ts` decodes `&lt;`), and a quote or an angle bracket is
 * punctuation around a URL far more often than it is part of one. The trailing-punctuation trim
 * below does the rest of the work — a regex cannot tell "the sentence ended" from "the path ends
 * in a full stop", so it deliberately over-matches here and is trimmed there.
 */
const URL_RUN = /https?:\/\/[^\s<>"'`]+/gi

/** Punctuation that ends a sentence rather than a URL. Trimmed from the tail, repeatedly. */
const TRAILING_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?', '"', "'", '”', '’', '…', '*'])

/** Closers that belong to the URL only when the URL also carries their opener. */
const BRACKET_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['(', ')'],
  ['[', ']'],
  ['{', '}'],
]

/**
 * How long a printed URL is allowed to get before the middle is elided.
 *
 * A Google Forms link is ~90 characters of opaque id. Printed whole it wraps across three lines of
 * an announcement and buries the professor's sentence; elided, the host and the start of the path
 * still say WHERE it goes, and the full address is on the `title` and in the `href` — so nothing
 * is hidden, it is only folded.
 */
export const MAX_LINK_LABEL = 52

/** Characters kept from the END of an elided URL. Enough for `/viewform`, `/j/93… `, `.pdf`. */
const LABEL_TAIL = 12

/**
 * The visible form of a URL: itself when it fits, head + `…` + tail when it does not.
 *
 * HEAD-BIASED on purpose. The first 39 characters carry the scheme, the host and the first path
 * segment — `https://docs.google.com/forms/d/e/…` — which is the part that answers "is this
 * Google Forms or is this a link I should not click". The tail is kept because the last segment
 * is usually the only human-readable one.
 */
export function linkLabel(url: string, max: number = MAX_LINK_LABEL): string {
  if (url.length <= max) return url
  const head = Math.max(1, max - LABEL_TAIL - 1)
  return `${url.slice(0, head)}…${url.slice(-LABEL_TAIL)}`
}

/**
 * Trims the punctuation a sentence put after a URL back off the match.
 *
 * TWO RULES, and the second is the one a naive trim gets wrong:
 *   · A trailing `.` `,` `!` `?` `:` `;` `"` `'` is sentence punctuation. Real URLs do not end
 *     in one, and "go to https://x.test/form." must not link to `…/form.`.
 *   · A trailing `)` is only sentence punctuation when the URL has no `(` of its own. Wikipedia's
 *     `…/Turing_(machine)` is a real address; "(see https://x.test/a)" is a parenthetical.
 *
 * Applied repeatedly, because `…/form).` is both at once.
 */
export function trimTrailingPunctuation(url: string): string {
  let out = url
  for (;;) {
    const last = out[out.length - 1]
    if (last === undefined) return out
    if (TRAILING_PUNCTUATION.has(last)) {
      out = out.slice(0, -1)
      continue
    }
    const pair = BRACKET_PAIRS.find(([, close]) => close === last)
    if (pair) {
      const opens = out.split(pair[0]).length - 1
      const closes = out.split(pair[1]).length - 1
      if (closes > opens) {
        out = out.slice(0, -1)
        continue
      }
    }
    return out
  }
}

/**
 * Plain text → alternating text and link spans, in order, losing not one character.
 *
 * THE INVARIANT THE TESTS PIN: joining `text` and `href` back together in order reproduces the
 * input exactly. That is what makes this safe to run on a professor's announcement — there is no
 * arrangement of punctuation, brackets or adjacent URLs that can silently eat a word.
 *
 * IDEMPOTENT BY CONSTRUCTION rather than by a guard: the function only ever reads the input
 * string, and a `text` span it produced can never contain a URL (the whole URL went into a link
 * span), so re-running it over the output's text spans finds nothing to do.
 */
export function linkifySpans(input: string): LinkifiedSpan[] {
  const text = input ?? ''
  if (!text) return []

  const spans: LinkifiedSpan[] = []
  let cursor = 0
  // A fresh regex per call: `URL_RUN` is /g and therefore stateful, and a shared `lastIndex` is a
  // bug that only shows up on the second announcement.
  const re = new RegExp(URL_RUN.source, 'gi')

  for (let match = re.exec(text); match !== null; match = re.exec(text)) {
    const raw = match[0]
    const href = trimTrailingPunctuation(raw)
    // `https://` with nothing after it is not an address. Left as text rather than linked to
    // a page that cannot exist.
    if (!/^https?:\/\/[^/\s]/i.test(href)) {
      re.lastIndex = match.index + raw.length
      continue
    }
    if (match.index > cursor) spans.push({ kind: 'text', text: text.slice(cursor, match.index) })
    spans.push({ kind: 'link', href, label: linkLabel(href) })
    cursor = match.index + href.length
    // The punctuation trimmed off the tail belongs to the prose, so the scan resumes AT it.
    re.lastIndex = cursor
  }

  if (cursor < text.length) spans.push({ kind: 'text', text: text.slice(cursor) })
  return spans
}

/** Whether a body has anything to linkify at all — for a caller that wants to skip the work. */
export function hasLink(text: string): boolean {
  return linkifySpans(text).some((span) => span.kind === 'link')
}
